import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  MessageSquare,
  Send,
  HelpCircle,
  GitBranch,
  Clock,
  Globe,
  StopCircle,
  Plus,
  Save,
  Play,
  Trash2,
  ArrowLeft,
  FileText,
  Sparkles,
  X,
  Webhook,
} from "lucide-react";
import {
  createFlow,
  updateFlow,
  getFlowById,
  generateFlowWithAI,
} from "../apis/flowAPI";
import TriggerNode from "../components/FlowNodes/TriggerNode";
import SendMessageNode from "../components/FlowNodes/SendMessageNode";
import AskQuestionNode from "../components/FlowNodes/AskQuestionNode";
import ConditionNode from "../components/FlowNodes/ConditionNode";
import DelayNode from "../components/FlowNodes/DelayNode";
import EndNode from "../components/FlowNodes/EndNode";
import WebhookNode from "../components/FlowNodes/WebhookNode";

// Register custom node types
const nodeTypes = {
  trigger: TriggerNode,
  sendMessage: SendMessageNode,
  askQuestion: AskQuestionNode,
  condition: ConditionNode,
  delay: DelayNode,
  end: EndNode,
  webhook: WebhookNode,
};

const FlowBuilder = () => {
  const { flowId: urlFlowId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodePanel, setShowNodePanel] = useState(true);
  const [flowId, setFlowId] = useState(urlFlowId || null);
  const [loading, setLoading] = useState(!!urlFlowId);
  const [saving, setSaving] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiDescription, setAiDescription] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  const onConnect = useCallback(
    (params) => {
      // For condition nodes, capture which handle was used (true/false)
      let conditionType = "always";
      let conditionValue = "";

      if (params.sourceHandle) {
        // Condition node has handles with IDs "true" or "false"
        if (params.sourceHandle === "true") {
          conditionType = "true";
        } else if (params.sourceHandle === "false") {
          conditionType = "false";
        }
      }

      const newEdge = {
        ...params,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: {
          conditionType,
          conditionValue,
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  // Get the current selected node from nodes array to ensure it's always up to date
  const currentSelectedNode = useMemo(() => {
    if (!selectedNode) return null;
    return nodes.find((n) => n.id === selectedNode.id) || selectedNode;
  }, [nodes, selectedNode]);

  // Load flow if editing
  useEffect(() => {
    const loadFlow = async () => {
      if (!urlFlowId) return;

      try {
        setLoading(true);
        const token = localStorage.getItem("userAccessToken");
        const response = await getFlowById(token, urlFlowId);
        const flow = response.data.flow;

        // Set flow metadata
        setFlowName(flow.name || "Untitled Flow");
        setFlowDescription(flow.description || "");
        setFlowId(flow.id);

        // Convert database nodes to ReactFlow format
        const reactFlowNodes = flow.nodes.map((node) => ({
          id: node.node_id,
          type: node.node_type,
          position: { x: node.position_x, y: node.position_y },
          data: node.config,
        }));
        setNodes(reactFlowNodes);

        // Convert database connections to ReactFlow edges
        const reactFlowEdges = flow.connections.map((conn) => {
          const edge = {
            id: `e${conn.source_node_id}-${conn.target_node_id}`,
            source: conn.source_node_id,
            target: conn.target_node_id,
            markerEnd: { type: MarkerType.ArrowClosed },
            data: {
              conditionType: conn.condition_type || "always",
              conditionValue: conn.condition_value || "",
            },
          };

          // For condition nodes, set sourceHandle based on conditionType
          if (
            conn.condition_type === "true" ||
            conn.condition_type === "false"
          ) {
            edge.sourceHandle = conn.condition_type;
          }

          return edge;
        });
        setEdges(reactFlowEdges);
      } catch (error) {
        console.error("Error loading flow:", error);
        alert("Failed to load flow");
        navigate("/flow-list");
      } finally {
        setLoading(false);
      }
    };

    loadFlow();
  }, [urlFlowId, navigate, setNodes, setEdges]);

  // Add new node
  const addNode = (type) => {
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: getDefaultNodeData(type),
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const getDefaultNodeData = (type) => {
    switch (type) {
      case "trigger":
        return { label: "Flow Trigger", keywords: [], triggerType: "keyword" };
      case "sendMessage":
        return { label: "Send Message", message: "", templateId: null };
      case "askQuestion":
        return { label: "Ask Question", question: "", variableName: "" };
      case "condition":
        return { label: "Condition", conditions: [] };
      case "delay":
        return { label: "Delay", duration: 5, unit: "seconds" };
      case "webhook":
        return {
          label: "Webhook",
          webhookUrl: "",
          method: "POST",
          headers: "{}",
          requestBody: "",
          inputVariable: "",
          responseVariable: "webhook_response",
          responseMessage: "Data fetched successfully",
        };
      case "end":
        return { label: "End Flow" };
      default:
        return { label: type };
    }
  };

  // Save flow
  const handleSaveFlow = async () => {
    if (!flowName.trim()) {
      alert("Please enter a flow name");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("userAccessToken");
      const flowData = {
        name: flowName.trim(),
        description: flowDescription.trim(),
        nodes: nodes.map((n) => ({
          nodeId: n.id,
          type: n.type,
          positionX: n.position.x,
          positionY: n.position.y,
          config: n.data,
        })),
        connections: edges.map((e) => ({
          sourceNodeId: e.source,
          targetNodeId: e.target,
          conditionType:
            e.data?.conditionType ||
            (e.sourceHandle === "true"
              ? "true"
              : e.sourceHandle === "false"
              ? "false"
              : "always"),
          conditionValue: e.data?.conditionValue || "",
        })),
        isActive: true,
      };

      let result;
      if (flowId) {
        result = await updateFlow(token, flowId, flowData);
        alert("Flow updated successfully!");
      } else {
        result = await createFlow(token, flowData);
        const newFlowId = result.data.flowId;
        setFlowId(newFlowId);
        navigate(`/flows/builder/${newFlowId}`, { replace: true });
        alert("Flow created successfully!");
      }
    } catch (error) {
      console.error("Error saving flow:", error);
      alert(error.response?.data?.error || "Failed to save flow");
    } finally {
      setSaving(false);
    }
  };

  // Delete node
  const handleDeleteNode = () => {
    if (selectedNode) {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) =>
        eds.filter(
          (e) => e.source !== selectedNode.id && e.target !== selectedNode.id
        )
      );
      setSelectedNode(null);
    }
  };

  // Generate flow with AI
  const handleGenerateWithAI = async () => {
    if (!aiDescription.trim()) {
      alert("Please enter a description of your flow");
      return;
    }

    try {
      setAiGenerating(true);
      const token = localStorage.getItem("userAccessToken");
      const response = await generateFlowWithAI(
        token,
        aiDescription,
        flowName || "AI Generated Flow"
      );

      const generatedFlow = response.data.flow;

      // Convert AI-generated flow to ReactFlow format
      const reactFlowNodes = generatedFlow.nodes.map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position || {
          x: Math.random() * 400,
          y: Math.random() * 400,
        },
        data: node.data || getDefaultNodeData(node.type),
      }));

      const reactFlowEdges = generatedFlow.connections.map((conn, idx) => {
        const edge = {
          id: `e${conn.source}-${conn.target}-${idx}`,
          source: conn.source,
          target: conn.target,
          markerEnd: { type: MarkerType.ArrowClosed },
          data: {
            conditionType: conn.conditionType || "always",
            conditionValue: conn.conditionValue || "",
          },
        };

        // For condition nodes, set sourceHandle
        if (conn.conditionType === "true" || conn.conditionType === "false") {
          edge.sourceHandle = conn.conditionType;
        }

        return edge;
      });

      // Set the generated nodes and edges
      setNodes(reactFlowNodes);
      setEdges(reactFlowEdges);

      // Set flow name if provided
      if (response.data.name && !flowName) {
        setFlowName(response.data.name);
      }

      // Close AI panel
      setShowAIPanel(false);
      setAiDescription("");

      alert("Flow generated successfully! You can now edit and save it.");
    } catch (error) {
      console.error("Error generating flow with AI:", error);
      alert(
        error.response?.data?.error ||
          "Failed to generate flow. Please try again."
      );
    } finally {
      setAiGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading flow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Bar - Enhanced */}
      <div className="bg-white border-b shadow-md">
        <div className="px-3 sm:px-4 lg:px-5 py-2">
          {/* First Row: Back Button, Name and Description in same line */}
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => navigate("/flow-list")}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Back to flows"
            >
              <ArrowLeft size={18} className="text-gray-600" />
            </button>

            {/* Flow Name - Takes more space */}
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                className="text-sm font-semibold w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                placeholder="Flow name *"
                autoFocus={!flowId}
              />
            </div>

            {/* Description - Compact */}
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowDescription}
                onChange={(e) => setFlowDescription(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                placeholder="Description (optional)"
              />
            </div>
          </div>

          {/* Second Row: Stats and Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-md">
                  <MessageSquare size={12} className="text-blue-600" />
                  <span className="font-medium text-blue-700">
                    {nodes.length}
                  </span>
                  <span className="text-gray-500">nodes</span>
                </div>
                <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 rounded-md">
                  <GitBranch size={12} className="text-green-600" />
                  <span className="font-medium text-green-700">
                    {edges.length}
                  </span>
                  <span className="text-gray-500">connections</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => navigate("/flow-list")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFlow}
                disabled={saving || !flowName.trim()}
                className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-1.5 text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm font-medium"
              >
                <Save size={16} />
                {saving ? "Saving..." : flowId ? "Save Changes" : "Save Flow"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* AI Generation Panel */}
        {showAIPanel && (
          <div className="w-80 bg-white border-r shadow-lg z-10 flex flex-col">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Sparkles className="text-purple-600" size={18} />
                <h3 className="font-semibold text-sm">AI Flow Generator</h3>
              </div>
              <button
                onClick={() => {
                  setShowAIPanel(false);
                  setAiDescription("");
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 p-3 overflow-y-auto">
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Describe your flow
                </label>
                <textarea
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
                  rows={6}
                  placeholder="Example: When user sends 'hi', send a welcome message, wait 5 seconds, then ask their name and save it as 'user_name'. If they say 'yes', send a confirmation message, otherwise end the flow."
                />
                <p className="text-[10px] text-gray-500 mt-1.5">
                  Describe your chatbot flow in natural language. The AI will
                  create the flow structure automatically.
                </p>
              </div>
              <div className="space-y-1.5">
                <button
                  onClick={handleGenerateWithAI}
                  disabled={aiGenerating || !aiDescription.trim()}
                  className="w-full bg-purple-600 text-white px-3 py-2 text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-1.5"
                >
                  {aiGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Generate Flow
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAIPanel(false);
                    setAiDescription("");
                  }}
                  className="w-full bg-gray-100 text-gray-700 px-3 py-1.5 text-xs rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Left Sidebar - Node Panel */}
        {showNodePanel && (
          <div className="w-56 bg-white border-r p-3 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Add Nodes</h3>
              <button
                onClick={() => setShowAIPanel(!showAIPanel)}
                className="p-1.5 hover:bg-purple-50 rounded-lg transition-colors"
                title="Generate flow with AI"
              >
                <Sparkles size={16} className="text-purple-600" />
              </button>
            </div>
            <div className="space-y-1.5">
              <NodeButton
                icon={<MessageSquare size={20} />}
                label="Trigger"
                onClick={() => addNode("trigger")}
              />
              <NodeButton
                icon={<Send size={20} />}
                label="Send Message"
                onClick={() => addNode("sendMessage")}
              />
              <NodeButton
                icon={<HelpCircle size={20} />}
                label="Ask Question"
                onClick={() => addNode("askQuestion")}
              />
              <NodeButton
                icon={<GitBranch size={20} />}
                label="Condition"
                onClick={() => addNode("condition")}
              />
              <NodeButton
                icon={<Clock size={20} />}
                label="Delay"
                onClick={() => addNode("delay")}
              />
              <NodeButton
                icon={<Webhook size={20} />}
                label="Webhook"
                onClick={() => addNode("webhook")}
              />
              <NodeButton
                icon={<StopCircle size={20} />}
                label="End Flow"
                onClick={() => addNode("end")}
              />
            </div>
          </div>
        )}

        {/* Main Canvas */}
        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            nodesConnectable={true}
            nodesDraggable={true}
            edgesUpdatable={true}
            connectionLineStyle={{ stroke: "#3b82f6", strokeWidth: 2 }}
            connectionLineType="smoothstep"
            defaultEdgeOptions={{
              type: "smoothstep",
              animated: false,
              style: { stroke: "#3b82f6", strokeWidth: 2 },
            }}
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        {/* Right Sidebar - Node Properties */}
        {currentSelectedNode && (
          <div className="w-72 bg-white border-l p-3 overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-sm">Node Properties</h3>
              <button
                onClick={handleDeleteNode}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <NodePropertyPanel node={currentSelectedNode} setNodes={setNodes} />
          </div>
        )}
      </div>
    </div>
  );
};

// Node Button Component
const NodeButton = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2 p-2 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
  >
    <div className="text-blue-600 text-sm">{icon}</div>
    <span className="font-medium text-xs">{label}</span>
  </button>
);

// Node Property Panel
const NodePropertyPanel = ({ node, setNodes }) => {
  const updateNodeData = (key, value) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id ? { ...n, data: { ...n.data, [key]: value } } : n
      )
    );
  };

  // Trigger Node
  if (node.type === "trigger") {
    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">Trigger Type</label>
          <select
            value={node.data.triggerType || "keyword"}
            onChange={(e) => updateNodeData("triggerType", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="keyword">Keyword</option>
            <option value="welcome">Welcome Message</option>
            <option value="menu">Main Menu</option>
            <option value="default">Default (Fallback)</option>
          </select>
        </div>
        {node.data.triggerType === "keyword" && (
          <div>
            <label className="block text-xs font-medium mb-1">
              Keywords (comma separated)
            </label>
            <input
              key={`keywords-${node.id}`}
              type="text"
              value={node.data.keywords?.join(", ") || ""}
              onChange={(e) =>
                updateNodeData(
                  "keywords",
                  e.target.value.split(",").map((k) => k.trim())
                )
              }
              className="w-full border rounded px-2 py-1.5 text-sm"
              placeholder="hi, hello, start"
            />
          </div>
        )}
      </div>
    );
  }

  // Send Message Node
  if (node.type === "sendMessage") {
    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">Message Text</label>
          <textarea
            key={`message-${node.id}`}
            value={node.data.message || ""}
            onChange={(e) => updateNodeData("message", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            rows={3}
            placeholder="Enter message to send..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">
            Use Template?
          </label>
          <select
            value={node.data.useTemplate ? "yes" : "no"}
            onChange={(e) =>
              updateNodeData("useTemplate", e.target.value === "yes")
            }
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="no">No, send text message</option>
            <option value="yes">Yes, use template</option>
          </select>
        </div>
      </div>
    );
  }

  // Ask Question Node
  if (node.type === "askQuestion") {
    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">Question</label>
          <textarea
            key={`question-${node.id}`}
            value={node.data.question || ""}
            onChange={(e) => updateNodeData("question", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            rows={2}
            placeholder="What question to ask?"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">
            Save Response As
          </label>
          <input
            key={`variableName-${node.id}`}
            type="text"
            value={node.data.variableName || ""}
            onChange={(e) => updateNodeData("variableName", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="variable_name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">
            Expected Answer Type
          </label>
          <select
            value={node.data.answerType || "text"}
            onChange={(e) => updateNodeData("answerType", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="email">Email</option>
            <option value="phone">Phone Number</option>
          </select>
        </div>
      </div>
    );
  }

  // Condition Node
  if (node.type === "condition") {
    const conditions = node.data.conditions || [];

    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1.5">Conditions</label>
          {conditions.map((cond, index) => (
            <div
              key={`condition-${node.id}-${index}`}
              className="border rounded p-2 mb-1.5 bg-gray-50"
            >
              <div className="mb-1.5">
                <label className="text-[10px] text-gray-600">Variable</label>
                <input
                  key={`condition-var-${node.id}-${index}`}
                  type="text"
                  value={cond.variable || ""}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].variable = e.target.value;
                    updateNodeData("conditions", newConditions);
                  }}
                  className="w-full border rounded px-2 py-1 text-xs"
                  placeholder="variable_name"
                />
              </div>
              <div className="mb-1.5">
                <label className="text-[10px] text-gray-600">Operator</label>
                <select
                  key={`condition-op-${node.id}-${index}`}
                  value={cond.operator || "equals"}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].operator = e.target.value;
                    updateNodeData("conditions", newConditions);
                  }}
                  className="w-full border rounded px-2 py-1 text-xs"
                >
                  <option value="equals">Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greater">Greater than</option>
                  <option value="less">Less than</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-600">Value</label>
                <input
                  key={`condition-val-${node.id}-${index}`}
                  type="text"
                  value={cond.value || ""}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].value = e.target.value;
                    updateNodeData("conditions", newConditions);
                  }}
                  className="w-full border rounded px-2 py-1 text-xs"
                  placeholder="comparison value"
                />
              </div>
              <button
                onClick={() => {
                  const newConditions = conditions.filter(
                    (_, i) => i !== index
                  );
                  updateNodeData("conditions", newConditions);
                }}
                className="text-red-600 text-[10px] mt-1 hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const newConditions = [
                ...conditions,
                { variable: "", operator: "equals", value: "" },
              ];
              updateNodeData("conditions", newConditions);
            }}
            className="text-blue-600 text-xs hover:underline"
          >
            + Add Condition
          </button>
        </div>
      </div>
    );
  }

  // Delay Node
  if (node.type === "delay") {
    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">Duration</label>
          <input
            type="number"
            value={node.data.duration || 5}
            onChange={(e) =>
              updateNodeData("duration", parseInt(e.target.value))
            }
            className="w-full border rounded px-2 py-1.5 text-sm"
            min="1"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Unit</label>
          <select
            value={node.data.unit || "seconds"}
            onChange={(e) => updateNodeData("unit", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="seconds">Seconds</option>
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
          </select>
        </div>
      </div>
    );
  }

  // API Call Node
  if (node.type === "apiCall") {
    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">Method</label>
          <select
            value={node.data.method || "GET"}
            onChange={(e) => updateNodeData("method", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">URL</label>
          <input
            type="text"
            value={node.data.url || ""}
            onChange={(e) => updateNodeData("url", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="https://api.example.com/endpoint"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">
            Headers (JSON)
          </label>
          <textarea
            value={node.data.headers || "{}"}
            onChange={(e) => updateNodeData("headers", e.target.value)}
            className="w-full border rounded px-2 py-1.5 font-mono text-xs"
            rows={2}
            placeholder='{"Authorization": "Bearer token"}'
          />
        </div>
      </div>
    );
  }

  // Webhook Node
  if (node.type === "webhook") {
    return (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium mb-1">
            Webhook URL *
          </label>
          <input
            key={`webhookUrl-${node.id}`}
            type="url"
            value={node.data.webhookUrl || ""}
            onChange={(e) => updateNodeData("webhookUrl", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="https://your-server.com/api/webhook"
          />
          <p className="text-[10px] text-gray-500 mt-0.5">
            Your webhook endpoint URL
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">HTTP Method</label>
          <select
            value={node.data.method || "POST"}
            onChange={(e) => updateNodeData("method", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">
            Input Variable
          </label>
          <input
            key={`inputVariable-${node.id}`}
            type="text"
            value={node.data.inputVariable || ""}
            onChange={(e) => updateNodeData("inputVariable", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="item, order_id, ticket_id, etc."
          />
          <p className="text-[10px] text-gray-500 mt-0.5">
            Variable name from previous question (e.g., if user entered order
            ID, use that variable name)
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">
            Request Headers (JSON)
          </label>
          <textarea
            key={`headers-${node.id}`}
            value={node.data.headers || "{}"}
            onChange={(e) => updateNodeData("headers", e.target.value)}
            className="w-full border rounded px-2 py-1.5 font-mono text-xs"
            rows={2}
            placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
          />
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">
            Request Body (JSON)
          </label>
          <textarea
            key={`requestBody-${node.id}`}
            value={node.data.requestBody || ""}
            onChange={(e) => updateNodeData("requestBody", e.target.value)}
            className="w-full border rounded px-2 py-1.5 font-mono text-xs"
            rows={3}
            placeholder='{"order_id": "{inputVariable}", "user_phone": "{phoneNumber}"}'
          />
          <p className="text-[10px] text-gray-500 mt-0.5">
            Use {"{inputVariable}"} to insert the input variable. Use{" "}
            {"{phoneNumber}"} for user's phone.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">
            Response Variable Name
          </label>
          <input
            key={`responseVariable-${node.id}`}
            type="text"
            value={node.data.responseVariable || "webhook_response"}
            onChange={(e) => updateNodeData("responseVariable", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="webhook_response"
          />
          <p className="text-[10px] text-gray-500 mt-0.5">
            Variable name to store webhook response (can be used in messages
            with {"{variableName}"})
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">
            Success Message Template
          </label>
          <textarea
            key={`responseMessage-${node.id}`}
            value={node.data.responseMessage || ""}
            onChange={(e) => updateNodeData("responseMessage", e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            rows={2}
            placeholder="Order {order_id} status: {status}. Total: {amount}"
          />
          <p className="text-[10px] text-gray-500 mt-0.5">
            Message to send after webhook succeeds. Use {"{fieldName}"} to
            access response data fields.
          </p>
        </div>
      </div>
    );
  }

  // End Node
  if (node.type === "end") {
    return (
      <div className="space-y-3">
        <div className="text-gray-600 text-xs">
          This node ends the conversation flow. No configuration needed.
        </div>
      </div>
    );
  }

  return (
    <div className="text-gray-500 text-xs">
      Select a node to edit its properties
    </div>
  );
};

export default FlowBuilder;
